<template>
    <div v-if="isVisible" class="gh-modal-overlay" @click="handleOverlayClick">
        <div :class="modalContainerClass" :style="modalStyle" @click.stop>
            <div class="gh-modal-header" :class="{ 'no-border': !title }">
                <slot name="title">
                    <span :class="modalTitleClass">{{ title }}</span>
                </slot>
                <span class="gh-modal-close" @click="reject">
                    <svg-icon
                        icon-name="close-regular"
                        :width="24"
                        :height="24"
                    ></svg-icon>
                </span>
            </div>
            <div class="gh-modal-body">
                <div class="gh-modal-body-scroll">
                    <slot>
                        <render-v-node :body="body"></render-v-node>
                    </slot>
                </div>
            </div>
            <div v-if="hasFooterSlot" class="gh-modal-footer">
                <slot name="footer">
                    <button
                        v-if="showReject"
                        type="button"
                        class="btn btn-reject"
                        @click="reject"
                    >
                        {{ cancelText }}
                    </button>
                    <button
                        v-if="showConfirm"
                        type="button"
                        class="btn btn-resolve"
                        @click="resolve"
                    >
                        {{ confirmText }}
                    </button>
                </slot>
            </div>
        </div>
    </div>
</template>

<script setup lang="ts">
    import Vue from 'vue';
    import {
        computed,
        defineComponent,
        h,
        nextTick,
        onBeforeUnmount,
        onMounted,
        onUpdated,
        reactive,
        ref,
        useSlots,
    } from 'vue';
    import DOMPurify from 'dompurify';
    import {
        type ModalStackEntry,
        pushModalStackEntry,
        releaseModalStackEntry,
    } from '@/components/services/gh-modal-stack-manager';
    import {
        ModalSize,
        ModalTitleAlign,
        type ModalOptions,
    } from '@/types/components/base-components.types';
    import SvgIcon from '@/components/base/SvgIcon.vue';

    interface ModalState {
        title: string;
        body: string;
        isVisible: boolean;
        showReject: boolean;
        showConfirm: boolean;
        cancelText: string;
        confirmText: string;
        onResolve: (() => void) | null;
        onReject: (() => void) | null;
        size: ModalSize;
        width: string | null;
        closeOnOverlay: boolean;
        closeOnEsc: boolean;
        titleAlign: ModalTitleAlign;
        isStatic: boolean;
    }

    interface ModalController {
        open: (options?: ModalOptions) => void;
        confirm: (options?: ModalOptions) => void;
        close: () => void;
    }

    interface SpawnedModalContext {
        modalRef: ModalController | null;
        dispose: () => void;
    }

    const RenderVNode = defineComponent({
        name: 'RenderVNode',
        props: {
            body: {
                type: String,
                default: '',
            },
        },
        setup(renderProps) {
            const rootRef = ref<HTMLElement | null>(null);
            const safeHtml = computed(() => {
                return DOMPurify.sanitize(renderProps.body, {
                    ALLOWED_TAGS: [
                        'a',
                        'b',
                        'br',
                        'code',
                        'div',
                        'em',
                        'i',
                        'img',
                        'li',
                        'ol',
                        'p',
                        'span',
                        'strong',
                        'u',
                        'ul',
                    ],
                    ALLOWED_ATTR: [
                        'href',
                        'target',
                        'rel',
                        'src',
                        'alt',
                        'title',
                        'style',
                    ],
                });
            });

            const syncInlineStyle = (): void => {
                const targetRootElement = rootRef.value as HTMLElement;
                nextTick(() => {
                    applyInlineStyleFromAttr(targetRootElement);
                });
            };

            onMounted(() => {
                syncInlineStyle();
            });

            onUpdated(() => {
                syncInlineStyle();
            });

            return () =>
                h('span', {
                    ref: rootRef,
                    domProps: {
                        innerHTML: safeHtml.value,
                    },
                });
        },
    });

    // ----- emits -----
    const emits = defineEmits<{
        (event: 'opened'): void;
        (event: 'closed'): void;
    }>();

    // ----- state -----
    const slots = useSlots();
    const modalInstanceId = Symbol('gh-modal-instance');
    const spawnedModalContextStack: SpawnedModalContext[] = [];
    const state = reactive<ModalState>({
        title: '',
        body: '',
        isVisible: false,
        showReject: true,
        showConfirm: false,
        cancelText: '關閉',
        confirmText: '確定',
        onResolve: null,
        onReject: null,
        size: ModalSize.Md,
        width: null,
        closeOnOverlay: true,
        closeOnEsc: true,
        titleAlign: ModalTitleAlign.Left,
        isStatic: false,
    });

    // ----- computed -----
    const hasFooterSlot = computed(() => {
        return Boolean(slots.footer) || state.showReject || state.showConfirm;
    });

    const modalContainerClass = computed(() => {
        return ['gh-modal-container', `gh-modal-${state.size}`];
    });

    const modalTitleClass = computed(() => {
        return ['gh-modal-title', `gh-modal-title-${state.titleAlign}`];
    });

    const modalStyle = computed(() => {
        return state.width ? { maxWidth: state.width } : {};
    });

    const title = computed(() => state.title);
    const body = computed(() => state.body);
    const isVisible = computed(() => state.isVisible);
    const showReject = computed(() => state.showReject);
    const showConfirm = computed(() => state.showConfirm);
    const cancelText = computed(() => state.cancelText);
    const confirmText = computed(() => state.confirmText);

    // ----- methods -----
    const hasOwn = <K extends keyof ModalOptions>(
        source: ModalOptions,
        key: K
    ): source is ModalOptions & Required<Pick<ModalOptions, K>> => {
        return Object.prototype.hasOwnProperty.call(source, key);
    };

    // 由呼叫端控制是否允許在同一個 modal instance 疊加顯示
    const resolveAllowMultiple = (options: ModalOptions): boolean => {
        return hasOwn(options, 'allowMultiple')
            ? Boolean(options.allowMultiple)
            : false;
    };

    // 動態產生的子 modal 一律關閉多開，避免遞迴建立實例
    const buildSpawnedModalOptions = (options: ModalOptions): ModalOptions => {
        return {
            ...options,
            allowMultiple: false,
        };
    };

    const removeSpawnedModalContext = (context: SpawnedModalContext): void => {
        const nextContextStack = spawnedModalContextStack.filter(
            (stackContext) => stackContext !== context
        );
        spawnedModalContextStack.splice(
            0,
            spawnedModalContextStack.length,
            ...nextContextStack
        );
        context.dispose();
    };

    // 當目前 modal 已顯示且允許多開時，建立新的 gh-modal 實例
    const createSpawnedModalContext = (): SpawnedModalContext | null => {
        const mountNode = document.createElement('div');
        document.body.appendChild(mountNode);

        let context!: SpawnedModalContext;
        const handleClosed = (): void => {
            removeSpawnedModalContext(context);
        };
        const dynamicVm = new Vue({
            render(createElement) {
                return createElement('gh-modal', {
                    ref: 'spawnedModalRef',
                    on: {
                        closed: handleClosed,
                    },
                });
            },
        }).$mount(mountNode);

        const modalRef = dynamicVm.$refs
            .spawnedModalRef as unknown as ModalController | null;

        context = {
            modalRef,
            dispose: () => {
                dynamicVm.$destroy();
                dynamicVm.$el?.parentNode?.removeChild(dynamicVm.$el);
            },
        };

        if (!modalRef || typeof modalRef.open !== 'function') {
            context.dispose();
            return null;
        }

        return context;
    };

    const createSpawnedModalRef = (): ModalController | null => {
        const context = createSpawnedModalContext();
        if (!context) {
            return null;
        }
        spawnedModalContextStack.push(context);
        return context.modalRef;
    };

    // close() 先關閉最上層動態 modal，確保行為符合堆疊順序
    const closeSpawnedModalIfExists = (): boolean => {
        while (spawnedModalContextStack.length > 0) {
            const latestContext =
                spawnedModalContextStack[spawnedModalContextStack.length - 1];

            if (
                latestContext.modalRef &&
                typeof latestContext.modalRef.close === 'function'
            ) {
                latestContext.modalRef.close();
                return true;
            }

            removeSpawnedModalContext(latestContext);
        }

        return false;
    };

    const closeSelf = (): void => {
        if (!state.isVisible) return;

        state.isVisible = false;
        releaseModalStackEntry(modalInstanceId);
        emits('closed');
    };

    const resolve = (): void => {
        state.onResolve?.();
        closeSelf();
    };

    const reject = (): void => {
        state.onReject?.();
        closeSelf();
    };

    const modalStackEntry: ModalStackEntry = {
        id: modalInstanceId,
        isVisible: () => state.isVisible,
        closeOnEsc: () => state.closeOnEsc,
        isStatic: () => state.isStatic,
        reject: () => reject(),
    };

    const openSelf = (options: ModalOptions = {}): void => {
        state.title = hasOwn(options, 'title') ? options.title || '' : '';
        state.body = hasOwn(options, 'body') ? options.body || '' : '';
        state.showReject = hasOwn(options, 'showReject')
            ? Boolean(options.showReject)
            : true;
        state.showConfirm = false;
        state.cancelText = hasOwn(options, 'cancelText')
            ? options.cancelText || '關閉'
            : '關閉';
        state.confirmText = hasOwn(options, 'confirmText')
            ? options.confirmText || '確定'
            : '確定';
        state.onResolve = hasOwn(options, 'onResolve')
            ? options.onResolve || null
            : null;
        state.onReject = hasOwn(options, 'onReject')
            ? options.onReject || null
            : null;
        state.size = hasOwn(options, 'size')
            ? options.size || ModalSize.Md
            : ModalSize.Md;
        state.width = hasOwn(options, 'width') ? options.width || null : null;
        state.closeOnOverlay = hasOwn(options, 'closeOnOverlay')
            ? Boolean(options.closeOnOverlay)
            : true;
        state.closeOnEsc = hasOwn(options, 'closeOnEsc')
            ? Boolean(options.closeOnEsc)
            : true;
        state.titleAlign = hasOwn(options, 'titleAlign')
            ? options.titleAlign || ModalTitleAlign.Left
            : ModalTitleAlign.Left;
        state.isStatic = hasOwn(options, 'isStatic')
            ? Boolean(options.isStatic)
            : false;

        state.isVisible = true;
        pushModalStackEntry(modalStackEntry);
        emits('opened');
    };

    const confirmSelf = (options: ModalOptions = {}): void => {
        openSelf(options);
        state.showConfirm = true;
    };

    // allowMultiple=true 且當前已開啟時，改由新實例承接顯示
    const open = (options: ModalOptions = {}): void => {
        if (resolveAllowMultiple(options) && state.isVisible) {
            const spawnedModalRef = createSpawnedModalRef();
            if (spawnedModalRef) {
                spawnedModalRef.open(buildSpawnedModalOptions(options));
                return;
            }
        }

        openSelf(options);
    };

    // confirm 走與 open 相同的多開策略
    const confirm = (options: ModalOptions = {}): void => {
        if (resolveAllowMultiple(options) && state.isVisible) {
            const spawnedModalRef = createSpawnedModalRef();
            if (
                spawnedModalRef &&
                typeof spawnedModalRef.confirm === 'function'
            ) {
                spawnedModalRef.confirm(buildSpawnedModalOptions(options));
                return;
            }
        }

        confirmSelf(options);
    };

    // 先嘗試關閉堆疊頂端子 modal，沒有子 modal 才關閉本體
    const close = (): void => {
        if (closeSpawnedModalIfExists()) {
            return;
        }
        closeSelf();
    };

    const handleOverlayClick = (): void => {
        if (state.closeOnOverlay) {
            reject();
        }
    };

    // 重新套用 style 字串，確保 innerHTML 渲染後的 inline style 持續生效
    const applyInlineStyleFromAttr = (containerElement: HTMLElement): void => {
        containerElement
            .querySelectorAll<HTMLElement>('[style]')
            .forEach((element) => {
                element.style.cssText = element.getAttribute('style') || '';
            });
    };

    // ----- lifecycle -----
    onBeforeUnmount(() => {
        releaseModalStackEntry(modalInstanceId);

        while (spawnedModalContextStack.length > 0) {
            const context = spawnedModalContextStack.pop();
            context?.dispose();
        }
    });

    // ----- expose -----
    defineExpose({
        open,
        confirm,
        close,
        resolve,
        reject,
    });
</script>

<style scoped lang="scss">
    :global(body.modal-open) {
        overflow: hidden;
    }

    .gh-modal-overlay {
        position: fixed;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
        display: flex;
        justify-content: center;
        align-items: center;
        background-color: rgba(0, 0, 0, 0.5);
        z-index: 9001;
    }

    .gh-modal-container {
        width: 90%;
        display: flex;
        flex-direction: column;
        background-color: #ffffff;
        border-radius: 8px;
        box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
        overflow: hidden;
        max-height: 90dvh;

        &.gh-modal-md {
            max-width: 540px;

            @include rwd($memberAreRWDsmall) {
                max-width: 290px;
            }
        }

        &.gh-modal-sm {
            max-width: 290px;
        }

        .gh-modal-header {
            display: flex;
            padding: 16px 16px 8px;
            color: $textColor45;
            font-size: 16px;
            line-height: 24px;
            border-bottom: 1px solid $grayColorE7;

            .gh-modal-title {
                width: 100%;

                &.gh-modal-title-left {
                    text-align: left;

                    @include rwd($memberAreRWDmini) {
                        text-align: center;
                    }
                }

                &.gh-modal-title-center {
                    text-align: center;
                }
            }

            .gh-modal-close {
                font-size: 24px;
                cursor: pointer;
            }
        }

        .gh-modal-body {
            min-height: 0;
            display: flex;
            flex: 1 1 auto;
            padding: 16px 8px 16px 16px;

            .gh-modal-body-scroll {
                flex: 1 1 auto;
                width: 100%;
                padding-right: 8px;
                color: $textColor888;
                font-size: 16px;
                line-height: 1.5;
                white-space: pre-line;
                overflow-y: auto;

                &::-webkit-scrollbar {
                    width: 0.75rem;
                }

                &::-webkit-scrollbar-thumb {
                    border-radius: 50px;
                }
            }
        }

        .gh-modal-footer {
            display: flex;
            padding: 8px 16px 16px;
            justify-content: center;
            gap: 8px;
        }

        .btn-reject {
            padding: 8px 48px;
            color: $textColor888;
            font-size: 14px;
            border: 1px solid $grayColorB0;
            border-radius: 8px;

            &:hover {
                opacity: 0.8;
            }

            &:active,
            &.active {
                top: 0;
            }
        }

        .btn-resolve {
            padding: 8px 48px;
            background: $blueColor_2C94FF;
            color: #ffffff;
            font-size: 14px;
            border: 1px solid $grayColorE7;
            border-radius: 8px;

            &:hover {
                opacity: 0.8;
            }

            &:active,
            &.active {
                top: 0;
            }
        }
    }
</style>
